<template>
  <form-wrapper
    @label-click="open"
    @focus.native="open"
    :inline="inline"
    :icon="icon"
    :icon-classes="iconClasses"
    :underlined="underlined"
    :label-classes="labelClasses"
    :label="label"
    :error="error"
    :required="required"
    v-click-outside.native="close"
    tabindex="0"
  >
    <button
      :class="[fieldClasses, getFieldClasses]"
      :disabled="disabled"
      @click="dropdownOpen = !dropdownOpen"
      class="border border-gray-400 font-serif px-4 h-12 w-full pl-4 flex items-center justify-between cursor-default focus:outline-none"
      type="button"
      tabindex="-1"
    >
      <span class="truncate mr-1">{{ getPlaceholder }}</span>
      <span
        v-if="model.length && !hideLength"
        class="font-bold font-serif rounded-full py-1 px-2 text-xxs bg-alpha-100 text-white"
      >{{ model.length }}</span>
    </button>
    <transition name="translate-y">
      <div
        v-if="dropdownOpen && !disabled"
        @click.stop
        class="absolute top-full inset-x-0 shadow-xl bg-white p-4 z-10 h-60 duration-150 overflow-x-auto"
      >
        <div ref="list" class="dropdown">
          <Checkboxes
            v-model="model"
            :name="name"
            :columns="3"
            :value-getter="valueGetter"
            :label-getter="labelGetter"
            :options="options"
            :switch="getSwitch"
            :disabled="disabled"
            :required="required"
            @change="onChange"
            @focusout="close"
          />
        </div>
      </div>
    </transition>
    <template #label>
      <slot name="label" />
    </template>
    <template #icon>
      <slot name="icon" />
    </template>
    <template #action v-if="!model.length || hideLength">
      <slot name="caret">
        <span class="form-caret text-gray-500 pointer-events-none" />
      </slot>
    </template>
  </form-wrapper>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import FormWrapper from './FormWrapper'
import Checkboxes from './Checkboxes'
import { resolveValue } from '../js/utils'

export default {
  components: {
    FormWrapper,
    Checkboxes
  },
  directives: {
    ClickOutside
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    fieldClasses: {
      type: [String, Array],
      default: null
    },
    labelClasses: {
      type: [String, Array],
      default: null
    },
    iconClasses: {
      type: [String, Array],
      default: null
    },
    error: {
      type: [String, Array],
      default: null
    },
    columns: {
      type: Number,
      default: 3
    },
    valueGetter: {
      type: String,
      default: 'value'
    },
    labelGetter: {
      type: String,
      default: 'label'
    },
    options: {
      type: Array,
      default: () => []
    },
    switch: {
      type: Boolean,
      default: false
    },
    hideLength: {
      type: Boolean,
      default: false
    },
    underlined: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dropdownOpen: false
    }
  },
  computed: {
    getSwitch () {
      return this.switch
    },
    getIconClass () {
      return `icon-${this.icon}`
    },
    getFieldClasses () {
      return {
        'border-danger focus:border-danger': this.error,
        'is-underlined': this.underlined,
        'has-icon': this.icon,
        'pr-0': this.model.length && this.underlined,
        'is-placeholder': !this.model.length
      }
    },
    getPlaceholder () {
      return !this.model.length
        ? this.placeholder
        : this.options
          .filter(option => this.model.includes(resolveValue(option, this.valueGetter)))
          .map(option => resolveValue(option, this.labelGetter))
          .join(', ')
    },
    model: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        this.$emit('change', value)
      }
    }
  },
  methods: {
    getId (index) {
      return this.name
        ? `${this.name}-${this._uid}-${index}`
        : `${this._uid}-${index}`
    },
    onChange (event) {
      this.$emit('change', event)
    },
    open () {
      this.dropdownOpen = true
    },
    close () {
      this.dropdownOpen = false
    }
  }
}
</script>

<style scoped>
.translate-y-enter, .translate-y-leave-to {
  @apply transform -translate-y-2 opacity-0;
}
</style>
